/**
 * admin/src/pages/BlogPage.tsx
 * Quản lý Blog — danh sách, xóa, tạo mới, rebuild index
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBlogList, useBlogMutations } from '../hooks/useBlog';
import {
  DataTable,
  Badge,
  Pagination,
  SearchInput,
  Modal,
  ErrorBanner,
} from '../components/ui/index';
import type { Column } from '../components/ui/index';
import type { AdminBlogPost } from '../types/admin';

type BlogPostRow = AdminBlogPost & Record<string, unknown>;


const PAGE_SIZE = 20;

export function BlogPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    posts,
    total,
    totalPages,
    isLoading: blogLoading,
    error,
    refetch,
  } = useBlogList({
    search,
    category,
    page,
    limit: PAGE_SIZE,
  });

  const { deletePost, rebuildIndex, isDeleting, isRebuilding } = useBlogMutations(refetch);

  function handleSearch(v: string) { setSearch(v); setPage(1); }
  function handleCategory(v: string) { setCategory(v); setPage(1); }

  function openDelete(slug: string) {
    setDeleteSlug(slug);
    setShowDeleteModal(true);
  }

  function closeDelete() {
    setDeleteSlug(null);
    setShowDeleteModal(false);
  }

  async function handleDelete() {
    if (!deleteSlug) return;
    await deletePost(deleteSlug);
    refetch();
    closeDelete();
  }

  const columns: Column<BlogPostRow>[] = [
    {
      key: 'title',
      label: 'Tiêu đề',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          {(row.cover_image as string | undefined) && (
            <img
              src={row.cover_image as string}
              alt=""
              style={{ width: 16, height: 16, borderRadius: 2, objectFit: 'cover', flexShrink: 0, marginTop: 3 }}
            />
          )}
          <div>
            <div className="cell-primary">{row.title as string}</div>
            <div className="cell-muted">
              <code style={{ fontSize: 11 }}>{row.slug as string}</code>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      width: '110px',
      render: (row) => <Badge variant="info">{row.category as string}</Badge>,
    },
    {
      key: 'tags',
      label: 'Tags',
      width: '140px',
      render: (row) => {
        const tags = (row.tags as string[]) ?? [];
        const visible = tags.slice(0, 2);
        const extra = tags.length - 2;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {visible.map((t) => (
              <span
                key={t}
                className="badge badge-info"
                style={{ fontSize: 10 }}
              >
                {t}
              </span>
            ))}
            {extra > 0 && (
              <span className="badge badge-info" style={{ fontSize: 10 }}>
                +{extra}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'author',
      label: 'Tác giả',
      width: '130px',
      render: (row) => <span className="cell-muted">{row.author as string}</span>,
    },
    {
      key: 'published_date',
      label: 'Ngày đăng',
      width: '110px',
      render: (row) => (
        <span className="cell-muted">
          {(row.published_date as string) || '—'}
        </span>
      ),
    },
    {
      key: 'is_published',
      label: 'Published',
      width: '90px',
      render: (row) =>
        (row.is_published as boolean | undefined) ? (
          <Badge variant="success">Live</Badge>
        ) : (
          <Badge variant="warning">Draft</Badge>
        ),
    },
    {
      key: 'actions',
      label: '',
      width: '110px',
      render: (row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Link
            to={`/admin/blog/${row.slug as string}/edit`}
            className="btn btn-sm btn-secondary"
            onClick={(e) => e.stopPropagation()}
          >
            ✏️ Sửa
          </Link>
          <button
            className="btn btn-sm btn-danger"
            onClick={(e) => {
              e.stopPropagation();
              openDelete(row.slug as string);
            }}
          >
            🗑
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header__left">
          <h1>Quản lý Blog</h1>
          <p>{total} bài viết trong hệ thống</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => rebuildIndex()}
            disabled={isRebuilding}
          >
            {isRebuilding ? '⏳ Đang rebuild...' : '🔄 Rebuild Index'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/admin/blog/new')}
          >
            + Bài viết mới
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <ErrorBanner message={error} onRetry={refetch} />
        </div>
      )}

      {/* Table Card */}
      <div className="card">
        <div className="table-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: 1 }}>
            <SearchInput
              value={search}
              onChange={handleSearch}
              placeholder="Tìm tiêu đề, slug..."
            />
            <select
              className="input"
              value={category}
              onChange={(e) => handleCategory(e.target.value)}
              style={{ minWidth: 150 }}
            >
              <option value="">Tất cả Category</option>
              <option value="toan">Toán</option>
              <option value="tieng-anh">Tiếng Anh</option>
              <option value="other">Other</option>
            </select>
          </div>
          <span className="table-title" style={{ whiteSpace: 'nowrap' }}>
            {total.toLocaleString('vi-VN')} bài
          </span>
        </div>

        <div className="table-wrapper">
          <DataTable<BlogPostRow>
            columns={columns as unknown as Column<BlogPostRow>[]}
            rows={posts as unknown as BlogPostRow[]}
            isLoading={blogLoading}
            emptyText="Chưa có bài viết nào"
          />
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          totalRows={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          hasPrev={page > 1}
          hasNext={page < totalPages}
        />
      </div>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={closeDelete}
        title="Xóa bài viết"
        actions={
          <>
            <button className="btn btn-secondary" onClick={closeDelete}>
              Hủy
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Đang xóa...' : '🗑 Xóa bài viết'}
            </button>
          </>
        }
      >
        <p style={{ color: 'var(--text-2)', lineHeight: 1.6 }}>
          Bạn có chắc muốn xóa bài viết{' '}
          <strong style={{ color: 'var(--text-1)' }}>
            &quot;{deleteSlug}&quot;
          </strong>
          ?
        </p>
        <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 8 }}>
          Hành động này không thể hoàn tác. File markdown trên R2 sẽ bị xóa và index sẽ được rebuild.
        </p>
      </Modal>
    </div>
  );
}
